import { createInfiniteQuery, createMutation, createQuery } from 'react-query-kit';

import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { BASE_URL, createData, fetchData, updateData } from 'services/http/http';
import { buildFilterUrl } from 'support/helpers/urls/urls';
import {
  DocumentTypeWithVirtual,
  MessageDTO,
  MessageV2DTO,
  PaginationDTO,
  RawTransactionDTO,
  SenderEnvelopeDTO,
  TransactionDTO,
  TransactionInternalType,
} from 'support/types';
import { Filter, FilterValue } from 'support/types/filters';
import portalSchema from 'support/types/schema-portal';

import { getMessageOrEnvelope } from '../helpers/helpers';
import { TRANSACTIONS_QUERY_KEY } from '../queryKeys';

type RawTransactionsResponseDTO = PaginationDTO<RawTransactionDTO>;

type TransactionsResponseDTO = PaginationDTO<TransactionDTO>;

export type RawTransactionsSchemaResponse =
  portalSchema.paths['/v1/transactions']['get']['responses']['200']['content']['application/json'];

type DropTransactionResponse =
  portalSchema.paths['/v1/transactions/{type}/{id}/drop']['put']['responses']['200']['content'];

type DropTransactionsResponse =
  portalSchema.paths['/v1/transactions/bulk/drop']['put']['responses']['200']['content']['application/json'];

type DeliverTransactionsResponse =
  portalSchema.paths['/v1/transactions/bulk/deliver']['put']['responses']['200']['content']['application/json'];

type RecoverTransactionResponse =
  portalSchema.paths['/v1/transactions/{id}/recover']['put']['responses']['200']['content'];

type DeliverMessageResponse =
  portalSchema.paths['/v1/transactions/receiver-envelope/{id}/deliver']['put']['responses']['200']['content'];

type GetTransactionResponse = {
  transaction: MessageDTO | SenderEnvelopeDTO;
  relatedTransactions: Array<Array<MessageDTO | SenderEnvelopeDTO>>;
};

export const useTransaction = createQuery<GetTransactionResponse, { transactionId?: string }, Error>({
  primaryKey: '/messages',
  queryFn: async ({ queryKey: [_, variables] }) => {
    const response = await fetchData<GetTransactionResponse>(`/v1/transactions/${variables.transactionId}`);

    return response.data;
  },
  enabled: (_, variables) => !!variables.transactionId,
});

export type GetTransactionMessageResponse = {
  transaction: MessageDTO;
  relatedTransactions: Array<MessageDTO>;
};

type GetSenderEnvelopeResponse = {
  transaction: SenderEnvelopeDTO;
  relatedTransactions: Array<MessageDTO>;
};

export const useTransactionMessage = createQuery<GetTransactionMessageResponse, { messageId?: string }, Error>({
  primaryKey: '/messages',
  queryFn: async ({ queryKey: [_, variables] }) => {
    const response = await fetchData<GetTransactionMessageResponse>(
      `/v1/transactions/${TransactionInternalType.message}/${variables.messageId}`,
    );

    return response.data;
  },
  enabled: (_, variables) => !!variables.messageId,
});

export const useSenderEnvelope = createQuery<GetSenderEnvelopeResponse, { senderEnvelopeId?: string }, Error>({
  primaryKey: '/senderEnvelopes',
  queryFn: async ({ queryKey: [_, variables] }) => {
    const response = await fetchData<GetSenderEnvelopeResponse>(
      `/v1/transactions/${TransactionInternalType.senderEnvelope}/${variables.senderEnvelopeId}`,
    );

    return response.data;
  },
  enabled: (data, variables) => !!variables.senderEnvelopeId,
});

export const useInfiniteTransactions = createInfiniteQuery<TransactionsResponseDTO, { query?: Filter }, Error>({
  primaryKey: TRANSACTIONS_QUERY_KEY,
  queryFn: async ({ queryKey: [, variables], pageParam, signal }) => {
    const urlParams = buildFilterUrl({ ...variables.query, cursor: pageParam }, { forApiRequest: true });

    // The signal cancels the request when a newer search replaces this query.
    const { data } = await fetchData<RawTransactionsResponseDTO>('/v1/transactions' + urlParams, {
      config: { signal },
    });

    return {
      ...data,
      items: data.items.map((transaction) => ({
        ...transaction,
        internalType: determineInternalType(transaction),
      })),
    };
  },
  getNextPageParam: (lastPage) => lastPage.nextCursor,
  getPreviousPageParam: (_, allPages) => allPages?.at(-1)?.previousCursor,
});

export const useTransactions = createQuery<TransactionsResponseDTO, { query?: Filter }, Error>({
  primaryKey: '/transactions/paginated',
  queryFn: async ({ queryKey: [, variables] }) => {
    const urlParams = buildFilterUrl(variables.query);

    const { data } = await fetchData<RawTransactionsResponseDTO>('/v1/transactions' + urlParams);

    return {
      ...data,
      items: data.items.map((transaction) => ({
        ...transaction,
        internalType: determineInternalType(transaction),
      })),
    };
  },
});

export type BulkActionVariables = {
  items: Array<{ transactionId: string }>;
};

type DropTransactionVariables = {
  id: string;
  reason: string;
  type: TransactionInternalType;
};

type DropTransactionsVariables = BulkActionVariables & {
  reason: string;
};

export const useDropTransaction = createMutation<void, DropTransactionVariables, Error>({
  mutationFn: async ({ id, reason, type }) => {
    await updateData<DropTransactionResponse>(`/v1/transactions/${type}/${id}/drop`, { reason });
  },
});

export const useDropTransactions = createMutation<DropTransactionsResponse, DropTransactionsVariables, Error>({
  mutationFn: async ({ items, reason }) => {
    const { data } = await updateData<DropTransactionsResponse>(`/v1/transactions/bulk/drop`, { items, reason });
    return data;
  },
});

export const useDeliverTransactions = createMutation<DeliverTransactionsResponse, BulkActionVariables, Error>({
  mutationFn: async ({ items }) => {
    // mock
    // return { success: true, data: { succeded: ['1', '2'], failed: [] } };
    const { data } = await updateData<DropTransactionsResponse>(`/v1/transactions/bulk/deliver`, { items });
    return data;
  },
});

type RecoverTransactionVariables = {
  id: string;
};

export const useRecoverTransaction = createMutation<void, RecoverTransactionVariables, Error>({
  mutationFn: async ({ id }) => {
    const { data } = await updateData<RecoverTransactionResponse>(`/v1/transactions/${id}/recover`, {});
    return data;
  },
});

type RecoverTransactionsResponse =
  portalSchema.paths['/v1/transactions/bulk/recover']['put']['responses']['200']['content']['application/json'];

export const useRecoverTransactions = createMutation<RecoverTransactionsResponse, BulkActionVariables, Error>({
  mutationFn: async ({ items }) => {
    const { data } = await updateData<RecoverTransactionsResponse>(`/v1/transactions/bulk/recover`, { items });
    return data;
  },
});

type ForceDeliveryVariables = {
  receiverEnvelopeId: string;
};

export const useForceDelivery = createMutation<void, ForceDeliveryVariables, Error>({
  mutationFn: async ({ receiverEnvelopeId }) => {
    await updateData<DeliverMessageResponse>(`/v1/transactions/receiver-envelope/${receiverEnvelopeId}/deliver`);
  },
});

export const downloadAsCSV = async (message: MessageV2DTO) => {
  const nonVirtualDocumentType = mapVirtualToActualDocumentType(message.type as DocumentTypeWithVirtual);
  window.open(`${BASE_URL}v1/transactions/${nonVirtualDocumentType}/${message.id}/csv/download`, '_blank');
};

export const getTransactionsCount = async (filterValues: Array<FilterValue>, signal?: AbortSignal): Promise<number> => {
  const urlParams = buildFilterUrl({ filterValues }, { forApiRequest: true });
  const { data } = await fetchData<{ count: number }>(`/v1/transactions/count${urlParams}`, {
    config: { signal },
  });

  return data.count;
};

export const exportTransactions = async (filterValues: Array<FilterValue>, signal?: AbortSignal): Promise<void> => {
  const urlParams = buildFilterUrl({ filterValues }, { forApiRequest: true });
  const { data, headers } = await fetchData<Blob>(`/v1/transactions/export${urlParams}`, {
    config: { responseType: 'blob', signal, timeout: 0 },
  });
  const filename = parseContentDispositionFilename(
    headers['content-disposition'] as string | undefined,
    `transactions_${new Date().toISOString().slice(0, 10)}.csv`,
  );

  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadAsPDF = async (message: MessageV2DTO) => {
  const nonVirtualDocumentType = mapVirtualToActualDocumentType(message.type as DocumentTypeWithVirtual);
  window.open(`${BASE_URL}v1/transactions/${nonVirtualDocumentType}/${message.id}/pdf/download`, '_blank');
};

type ShareTransactionErrorRequest =
  portalSchema.paths['/v1/transactions/{id}/share-error']['post']['requestBody']['content']['application/json'];
type ShareTransactionErrorResponse =
  portalSchema.paths['/v1/transactions/{id}/share-error']['post']['responses']['200']['content']['application/json'];

export const useShareTransactionError = createMutation<
  ShareTransactionErrorResponse,
  ShareTransactionErrorRequest & { id: string },
  Error
>({
  mutationFn: async ({ id, email }) => {
    const { data } = await createData<ShareTransactionErrorResponse>(`/v1/transactions/${id}/share-error`, {
      email,
    });
    return data;
  },
});

type ConfirmOrRejectOrderRequest = Omit<
  portalSchema.paths['/v1/transactions/orders/{id}/respond']['post']['requestBody']['content']['application/json'],
  'type'
>;

type ConfirmOrRejectOrderResponse =
  portalSchema.paths['/v1/transactions/orders/{id}/respond']['post']['responses']['200']['content']['application/json'];

type OrderResponseType = 'ACCEPT' | 'REJECT';

// Common function to create order response mutations (confirm or reject)
const createOrderResponseMutation = (type: OrderResponseType) =>
  createMutation<
    {
      transactionId: string;
      transactionType: TransactionInternalType;
    },
    ConfirmOrRejectOrderRequest & { messageId: string },
    Error
  >({
    mutationFn: async ({ messageId, reason }) => {
      const { data } = await createData<ConfirmOrRejectOrderResponse>(`/v1/transactions/orders/${messageId}/respond`, {
        type,
        reason,
      });
      const createdSenderEnvelopeId = data.procuros_transaction_id;
      return await getMessageOrEnvelope(createdSenderEnvelopeId, true);
    },
  });

export const useConfirmOrder = createOrderResponseMutation('ACCEPT');
export const useRejectOrder = createOrderResponseMutation('REJECT');

export const downloadReceiverEnvelope = async (receiverEnvelopeId: string) => {
  window.open(`${BASE_URL}v1/transactions/receiver-envelope/${receiverEnvelopeId}/download`, '_blank');
};

export const downloadSenderEnvelope = async (senderEnvelopeId: string) => {
  window.open(`${BASE_URL}v1/transactions/sender-envelope/${senderEnvelopeId}/download`, '_blank');
};

type ForceProcessingMessageRequest =
  portalSchema.paths['/v1/transactions/message/{message}/force-processing']['put']['parameters']['path'];

export const useForceProcessingMessage = createMutation<void, ForceProcessingMessageRequest, Error>({
  mutationFn: async ({ message }) => {
    await updateData(`/v1/transactions/message/${message}/force-processing`);
  },
});

const determineInternalType = (transaction: RawTransactionDTO): TransactionInternalType => {
  if (transaction.messageId) {
    return TransactionInternalType.message;
  }

  return TransactionInternalType.senderEnvelope;
};

const parseContentDispositionFilename = (header: string | undefined, fallback: string): string => {
  if (!header) {
    return fallback;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through
    }
  }
  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1] : fallback;
};
